import { api } from '@/api';
import type { DatabaseChangeJobInfoDto } from '@/api/generator';
import { stopErrorPropagation } from '@/errorHandling';
import { isServerFailure } from '@/errorHandling/typeGuards';
import { useTimeoutPoll } from '@vueuse/core';
import { onMounted, ref } from 'vue';

interface UseDatabaseJobStateOptions {
  onSuccess: (lastJobHasCompleted: boolean, preventNextNavigation?: boolean) => void;
  onFailure: () => void;
}

/**
 * Get the State of the current database change operation
 */
export function useDatabaseJobState(options: UseDatabaseJobStateOptions) {
  const { onSuccess, onFailure } = options;

  /**
   * JobState-Transitions:
   * "Not Running" --> "Running" --> "Completed"
   *  or
   * "Not Running" --> "Running" --> "Failed"
   *
   *  If we see the state "Not Running" again after the system has rebooted,
   *  we know we are dealing with a completely new Database
   **/
  const jobState = ref<DatabaseChangeJobInfoDto>();
  const isRebooting = ref(false);

  async function getJobState() {
    const { result: currentJobState } = await api.installApi.getUpdateConnectionStringsStateAsync([
      (error: unknown) => {
        // assume Server is rebooting if there is a connection error
        if (error instanceof Error && (isServerFailure(error) || error.name === 'FetchError')) {
          isRebooting.value = true;
        }
        // Returns `error` rather than suppressing it directly; the trailing
        // `stopErrorPropagation` below actually stops the chain.
        return error instanceof Error ? error : undefined;
      },
      stopErrorPropagation,
    ]);

    jobState.value = currentJobState;

    if (!jobState.value) {
      return;
    }

    switch (jobState.value.state) {
      case 'Completed':
        if (!jobState.value?.rebootRequired || jobState.value?.rebootIsDone) {
          pausePollingForDatabaseJobState();
          onSuccess(true, true);
          isRebooting.value = false;
          jobState.value = undefined;
        }

        break;

      case 'NotRunning':
        pausePollingForDatabaseJobState();
        onSuccess(true, true);
        break;

      case 'Failed':
        pausePollingForDatabaseJobState();
        onFailure();
        break;

      case 'Running':
      default:
        break;
    }
  }

  const {
    pause: pausePollingForDatabaseJobState,
    resume: resumePollingForDatabaseJobState,
    isActive: isActivePollingForDatabaseJobState,
  } = useTimeoutPoll(getJobState, 500);

  onMounted(() => {
    resumePollingForDatabaseJobState();
  });

  return {
    jobState,
    isActive: isActivePollingForDatabaseJobState,
    isRebooting,
    resumePollingForDatabaseJobState,
  };
}
